
import React, { useEffect, useMemo, useState } from 'react'
import CommonCard from '../global/card'
import CommonSkeletonLoader from '../global/loading'
import SOURCES from '../../utils/constants'
import { useGenericQuery } from '../../queries/use-generic-query'
import { QUERY_KEYS } from '../../lib/query-constants'
import useDebounce from '../../utils/customHooks'
import EnhancedAffiliatesCustomersList from './enhancedAffiliatesCustomersList'
import CommonPagination from '../global/pagination'
import EnhancedAdvanceSearchAffiliates from './enhancedAdvanceSearchAffiliates'
import dayjs from 'dayjs'

const EnhancedAffliatesCustomers = ({ currentUser }) => {
    const affiliateId = currentUser?.user?._id;

    const [searchInput, setSearchInput] = useState("")
    const [sort, setSort] = useState({ sortType: "", sortOrder: 1 });
    const [showAdvanceSearch, setShowAdvanceSearch] = useState(false);
    const [affiliatesCustomersViewType, setAffiliatesCustomersViewType] = useState(() => {
        return localStorage.getItem("affiliatesCustomersViewType") || "LIST";
    });

    const [pagination, setPagination] = useState({
        offset: 0,
        limit: SOURCES.DEFAULT_RECORDS_LISTING,
        page: 1,
    });

    const [paginationSearch, setPaginationSearch] = useState({
        offsetSearch: 0,
        limitSearch: SOURCES.DEFAULT_RECORDS_SEARCH,
    });

    // Initialize date range - 1 year before to 3 years after
    const currentDate = new Date();
    const after3Years = new Date(currentDate.getFullYear() + 3, currentDate.getMonth(), currentDate.getDate());
    const before1Year = new Date(currentDate.getFullYear() - 1, currentDate.getMonth(), currentDate.getDate());

    const [advanceSearch, setAdvanceSearch] = useState({
        advance_search_enabled: SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_NOT_FILTER,
        startDate: "",
        endDate: "",
        searchTerm: "",
        searchInput: ""
    });

    const debouncedSearchTerm = useDebounce(searchInput, 500);

    // Helper function to format date
    const getDate = (dateObj) => {
        if (!dateObj || typeof dateObj !== 'object' || !dateObj.getFullYear) {
            return "";
        }
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Key params for main listing
    const keyParamsForCustomers = {
        offset: pagination.offset,
        limit: pagination.limit,
        sortBy: sort.sortType,
        sortOrder: sort.sortOrder,
    };

    // Key params for search
    const searchKeyParams = {
        offset: paginationSearch.offsetSearch,
        limit: paginationSearch.limitSearch,
        q: debouncedSearchTerm,
        advance_search: advanceSearch.advance_search_enabled,
        startDate: advanceSearch.startDate ? getDate(advanceSearch.startDate) : "",
        endDate: advanceSearch.endDate ? getDate(advanceSearch.endDate) : "",
        affiliateId: affiliateId,
        searchTerm: advanceSearch.searchTerm || advanceSearch.searchInput,
    };

    // Main customers data query
    const { data: customersData, isLoading: customersDataLoading, isFetching: customersDataFetching } = useGenericQuery({
        endpoint: "admin/affiliatecommission",
        queryKey: QUERY_KEYS.affiliates.lists({ ...keyParamsForCustomers, affiliateId }),
        queryParams: { 
            ...keyParamsForCustomers,
            affiliateId 
        },
        onError: () => { },
        onSuccess: () => { },
    });

    // Search data query
    const { data: searchData, isInitialLoading: searchDataLoading } = useGenericQuery({
        endpoint: "admin/affiliatecommission/search",
        queryKey: QUERY_KEYS.affiliates.lists({ ...searchKeyParams }),
        queryParams: { ...searchKeyParams },
        onError: () => { },
        onSuccess: () => { },
        enabled: !!debouncedSearchTerm.trim() ||
            (advanceSearch.advance_search_enabled !== SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_NOT_FILTER &&
                advanceSearch.advance_search_enabled !== SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_CLOSED_SEARCH_FORM)
    });

    // Determine final data to display
    const getFinalData = useMemo(() => {
        if (debouncedSearchTerm ||
            (advanceSearch.advance_search_enabled !== SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_NOT_FILTER &&
                advanceSearch.advance_search_enabled !== SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_CLOSED_SEARCH_FORM)) {
            return searchData?.results || [];
        }
        return customersData?.results || [];
    }, [customersData, searchData, debouncedSearchTerm, advanceSearch.advance_search_enabled]);

    const isLoading = customersDataLoading || searchDataLoading;
    const dataExist = getFinalData && getFinalData.length > 0;

    // Reset advance search when search input changes
    useEffect(() => {
        setAdvanceSearch((prev) => ({
            ...prev,
            advance_search_enabled: SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_NOT_FILTER,
        }));
        setShowAdvanceSearch(false);
    }, [searchInput]);

    return (
        <>
            <CommonCard
                isSearch={{
                    show: true,
                    placeholder: "Search customers...",
                    searchInput: searchInput,
                    setSearchInput: setSearchInput
                }}
                isListGridView={{
                    show: true,
                    viewType: affiliatesCustomersViewType,
                    setViewType: setAffiliatesCustomersViewType,
                    viewFor: "affiliatesCustomersViewType"
                }}
                isAdvanceSearch={{
                    show: true,
                    children: <EnhancedAdvanceSearchAffiliates
                        isOpen={showAdvanceSearch}
                        advanceSearch={advanceSearch}
                        setShowAdvanceSearch={setShowAdvanceSearch}
                        setAdvanceSearch={setAdvanceSearch}
                    />
                }}
                isLoading={{
                    show: isLoading,
                    loaderCmp: (
                        <CommonSkeletonLoader
                            rows={9}
                            type={affiliatesCustomersViewType === "GRID" ? "card" : "table"}
                            tableLength={6}
                        />
                    ),
                }}
                className={(!isLoading && customersDataFetching) && "opacity-50"}
            >
                <EnhancedAffiliatesCustomersList
                    isLoading={isLoading}
                    customers={getFinalData}
                    sort={sort}
                    setSort={setSort}
                    viewType={affiliatesCustomersViewType}
                />

                {(dataExist && !searchInput &&
                    advanceSearch.advance_search_enabled === SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_NOT_FILTER) &&
                    customersData?.totalRecords > SOURCES.DEFAULT_RECORDS_LISTING && (
                        <CommonPagination
                            currentPage={pagination.page}
                            defaultLimit={pagination.limit}
                            totalRecords={customersData?.totalRecords}
                            setCurrentPage={setPagination}
                            activePageRecords={getFinalData?.length || 0}
                            paginationFor="Customers"
                            className="mt-4"
                        />
                    )}
            </CommonCard>
        </>
    )
}

export default EnhancedAffliatesCustomers